import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { Reveal, Magnetic } from "../lib/motion";

const VIDEO_SRC = "/nexsys-video.mp4?v=5"; // Nexis highlight film (H.264 Main/faststart)
const POSTER =
  "https://images.unsplash.com/photo-1637166185518-058f5896a2e9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function Showreel() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <section
      id="showreel"
      className="relative border-t border-white/5 bg-nexis-bg/80 py-28 backdrop-blur-sm md:py-40"
      data-testid="showreel-section"
    >
      <div className="orb orb-blue left-[12%] top-[15%] h-[380px] w-[380px]" />
      <div className="mx-auto max-w-6xl px-6 md:px-12">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— HIGHLIGHT REEL</span>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-light tracking-tight lg:text-6xl">
              NexSys <span className="text-gradient">Video</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="max-w-sm text-sm leading-relaxed text-white/50">
              A cinematic look inside how Nexis operates — infrastructure, transformation, and intelligence, in motion.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <motion.button
            onClick={() => setOpen(true)}
            data-testid="showreel-play"
            className="group relative mt-14 block w-full overflow-hidden rounded-[2rem] glass"
            whileHover={{ scale: 1.005 }}
            transition={{ type: "spring", stiffness: 160, damping: 20 }}
          >
            <div className="relative aspect-video w-full overflow-hidden rounded-[2rem]">
              <img
                src={POSTER}
                alt="NexSys highlight video"
                className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-nexis-bg/80 via-nexis-bg/20 to-transparent" />

              {/* play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Magnetic strength={0.35}>
                  <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-nexis-blue/90 text-black shadow-[0_0_50px_rgba(0,200,255,0.5)] transition-transform group-hover:scale-110">
                    <span className="absolute inset-0 rounded-full bg-nexis-blue/40 blur-md" />
                    <Play className="relative ml-1 h-8 w-8 fill-black" />
                  </span>
                </Magnetic>
              </div>

              {/* corner meta */}
              <div className="absolute bottom-6 left-6 flex items-center gap-3 font-mono text-xs text-white/70">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-nexis-blue" /> NEXIS.TEAM
                </span>
                <span className="text-white/30">// SHOWREEL 2026</span>
              </div>
              <div className="absolute right-6 top-6 rounded-full border border-white/15 bg-black/30 px-3 py-1 font-mono text-[10px] tracking-widest text-white/60 backdrop-blur">
                WATCH FILM
              </div>
            </div>
          </motion.button>
        </Reveal>
      </div>

      {/* video modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 p-4 backdrop-blur-md"
            data-testid="showreel-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 200, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl overflow-hidden rounded-2xl glass"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white/70 backdrop-blur hover:bg-black/70 hover:text-white"
                data-testid="showreel-close"
                aria-label="Close video"
              >
                <X className="h-5 w-5" />
              </button>
              <video
                src={VIDEO_SRC}
                poster={POSTER}
                controls
                autoPlay
                playsInline
                className="aspect-video w-full bg-black"
                data-testid="showreel-video"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
